/* eslint-disable @next/next/no-img-element */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import { AdvancedMarker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { Marker, MarkerClusterer } from "@googlemaps/markerclusterer";

import { IBranch } from "@/app/[lang]/locations/helpers";
import AppText from "../other/AppText";
import { AppIconButton } from "../buttons/AppIconButton";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/mediaQuery";
import { ChevronRight } from "lucide-react";

const Markers = ({
  mapZoom,
  branches,
  moreDetails,
  //   setMapZoom,
  //   setMapCenter,
  multiMarkers,
  nearestMarkers,
}: {
  mapZoom: number;
  branches: IBranch[];
  moreDetails: string;
  multiMarkers?: boolean;
  nearestMarkers: any;
  //   setMapZoom?: (zoom: number) => void;
  //   setMapCenter?: (coord: { lat: number; lng: number }) => void;
}) => {
  const map = useMap();
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
  const [selectedMarker, setSelectedMarker] = useState<IBranch | null>(null);
  const isSmallScreen = useMediaQuery("(max-width: 768px)");

  const clusterer = useRef<MarkerClusterer>();

  useEffect(() => {
    setSelectedMarker(null);
  }, [mapZoom]);

  useEffect(() => {
    if (!selectedMarker) return;
    const infoWindowPosition = { lat: selectedMarker?.lat, lng: selectedMarker?.lng };

    if (map?.getBounds()?.contains(infoWindowPosition)) {
      map.panTo(infoWindowPosition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarker]);

  useEffect(() => {
    if (!map) return;

    if (!clusterer.current) {
      clusterer.current = new MarkerClusterer({
        map,
        renderer: {
          render: ({ markers, count, position }) => {
            return new google.maps.Marker({
              position: {
                lat: position.lat(),
                lng: position.lng(),
              },
              label: {
                text: String(markers?.length),
                color: "white",
              },
              icon: "/icons/cluster.svg",
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            });
          },
        },
      });
    }
  }, [map]);

  useEffect(() => {
    clusterer.current?.clearMarkers();

    clusterer.current?.addMarkers(Object.values(markers));
  }, [markers]);

  const setMarkerRef = (marker: Marker | null, key: string) => {
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      if (marker) {
        return { ...prev, [key]: marker };
      } else {
        const newMarkers = { ...prev };

        delete newMarkers[key];

        return newMarkers;
      }
    });
  };

  const closeInfoWindow = () => setSelectedMarker(null);

  const handleMarkerClick = (branch: any) => {
    // ! do we zoom on marker click?
    // setMapZoom && setMapZoom(10);
    // setMapCenter && setMapCenter({ lat: branch.lat, lng: branch.lng });
    multiMarkers && setSelectedMarker(branch);
  };

  return (
    <>
      {(branches || []).map((branch: any) => {
        // console.log("branch id for marker", branch.id);

        return (
          <AdvancedMarker
            className={cn(multiMarkers ? "advancedMarker" : "")}
            key={branch.id}
            position={branch}
            ref={(marker) => setMarkerRef(marker, branch.id)}
            onClick={() => handleMarkerClick(branch)}
          >
            <img
              // src={selectedMarker?.id === branch.id ? "/icons/pin-selected.svg" : "/icons/pin.svg"}
              src={nearestMarkers[branch.id] ? "/icons/ws_pin_light.png" : "/icons/ws_pin.png"}
              alt={`${selectedMarker?.name} marker`}
              className="w-[35px] h-[50px]"
            />
          </AdvancedMarker>
        );
      })}

      {multiMarkers && selectedMarker && !isSmallScreen && (
        <InfoWindow position={selectedMarker} onCloseClick={closeInfoWindow}>
          <InfoWindowContent label={moreDetails} selectedMarker={selectedMarker} />
        </InfoWindow>
      )}

      {multiMarkers && selectedMarker && isSmallScreen && (
        <div className="left-6 bottom-6 absolute max-w-[200px] shadow-md ">
          <div className="relative px-4 py-4 bg-white rounded">
            <button
              id="button-markers-close-infoWindow"
              onClick={closeInfoWindow}
              className="close-infoWindow-custom-button"
            >
              <span />
            </button>
            <InfoWindowContent label={moreDetails} selectedMarker={selectedMarker} />
          </div>
        </div>
      )}
    </>
  );
};

export default Markers;

const InfoWindowContent = ({ selectedMarker, label }: { selectedMarker: IBranch; label: string }) => {
  return (
    <div className="flex flex-col pb-1 bg-white translate-[50%_51%]">
      <AppText
        type="TITLE_MEDIUM"
        className="text-ellipsis break-keep flex-nowrap whitespace-nowrap py-0 pr-5 mb-1 overflow-hidden"
      >
        {selectedMarker.name}
      </AppText>

      {selectedMarker.phone && (
        <AppText type="BODY_SMALL" className="break-keep text-ellipsis flex-nowrap whitespace-nowrap overflow-hidden">
          {selectedMarker.phone}
        </AppText>
      )}

      <AppText
        type="BODY_SMALL"
        className="break-keep text-ellipsis flex-nowrap whitespace-nowrap py-0 mb-2 overflow-hidden"
      >
        {selectedMarker.address ? (
          <>
            {selectedMarker.address} <br />
          </>
        ) : null}
        {`${selectedMarker.state} ${selectedMarker.zipCode}`}
      </AppText>

      <AppIconButton
        intent="ghost"
        label={label}
        link={`/locations/${selectedMarker.stateSlug.replaceAll(" ", "-").toLowerCase()}/${selectedMarker.slug}`}
        icon={<ChevronRight size={20} aria-label="chevron right icon" />}
        iconOnRightSide
        className="after:hidden"
      />
    </div>
  );
};
